/**
 * Plans v2 card 3.1: ONE place that says what the signed-in plan allows
 * locally. Every create point asks `requireLocalCapacity` (plans/localGate.js)
 * instead of comparing plan names.
 *
 * Fed by, in order of trust:
 *   1. the signed plan claim (card 2.4, `source: 'claim'`);
 *   2. `GET /auth/usage` limits (`source: 'usage'`);
 *   3. the plan name alone (`source: 'plan'`): paid plans are unlimited
 *      locally, anything else gets Free's caps until the server answers.
 * Signed out, it is Free (`source: 'default'`).
 *
 * A recorded demo or e2e run is not a user and has no account to ask: it is
 * unlimited (`source: 'synthetic'`), whatever any setter is told, so a
 * storyboard never walks into an upgrade modal. Only in a demo/e2e BUILD
 * (platform/gateExempt.js): a release build never lifts the limits for a
 * runtime flag.
 */
import { create } from 'zustand';
import { defaultLocalLimits, localLimitsFrom, checkLimit } from '../plans/localLimits';
import { isGateExemptRun } from '../platform/gateExempt';

const synthetic = () => ({
  plan: 'synthetic',
  limits: defaultLocalLimits('basic'),
  capabilities: [],
  capabilitiesKnown: false,
  source: 'synthetic',
});

const initial = () => (isGateExemptRun() ? synthetic() : {
  plan: 'free',
  limits: defaultLocalLimits('free'),
  capabilities: [],
  // True once the server said which capabilities this plan has (claim, or a
  // /auth/usage answer): `[]` then means "none", not "not asked yet".
  capabilitiesKnown: false,
  source: 'default',
});

export const useEntitlementsStore = create((set, get) => ({
  ...initial(),

  /** A plan name is known (sign-in, session restore, plan_updated) but not its limits yet. */
  setPlan: (plan) => {
    if (isGateExemptRun()) return set(synthetic());
    const name = String(plan || 'free').toLowerCase();
    // A known plan's server limits are kept if the name did not change.
    if (get().plan === name && get().source !== 'default') return;
    // Another plan's capabilities would be stale: unknown until the server answers.
    set({ plan: name, limits: defaultLocalLimits(name), capabilities: [], capabilitiesKnown: false, source: 'plan' });
  },

  /**
   * The server's answer: `{ plan, limits, capabilities, source }` where
   * `limits` is the server's `{ local_*: n | null }` map.
   */
  setSnapshot: ({ plan, limits, capabilities, source = 'usage' } = {}) => {
    if (isGateExemptRun()) return set(synthetic());
    const name = String(plan || get().plan || 'free').toLowerCase();
    const known = Array.isArray(capabilities);
    set({
      plan: name,
      limits: localLimitsFrom(limits, defaultLocalLimits(name)),
      capabilities: known ? capabilities : get().capabilities,
      capabilitiesKnown: known || get().capabilitiesKnown,
      source,
    });
  },

  reset: () => set(initial()),
}));

/** `{ ok: true }` or `{ ok: false, kind, limit, used, adding, requiredPlan }`. */
export const checkLocalLimit = (kind, used, adding = 1) =>
  checkLimit(useEntitlementsStore.getState().limits, kind, used, adding);
